import Dexie, { Table } from 'dexie'
import {
  Task,
  Section,
  Setting,
  UserProfile,
  TimeEntry,
  Tag,
  Project,
  Goal,
  LifeSpace,
  SyncMetadata,
  SyncQueueItem,
  CreateTask,
  CreateSection,
  CreateSetting,
  CreateUserProfile,
  CreateTimeEntry,
  CreateTag,
  CreateProject,
  CreateGoal,
  UpdateTask,
  UpdateSection,
  UpdateSetting,
  UpdateUserProfile,
  UpdateTimeEntry,
  UpdateTag,
  UpdateProject,
  UpdateGoal,
  DB_VERSION,
  DB_NAME,
  DEFAULT_TASK_ATTENTION,
  DEFAULT_TASK_STATUS,
  DEFAULT_TASK_CATEGORY,
  DEFAULT_USER_PREFERENCES,
  DEFAULT_USER_STATISTICS
} from './models'
import type { AvatarCategory, Avatar } from './types'

/**
 * Flag to bypass auto-timestamp/synced hooks during sync-engine writes.
 * When true, db.tasks.update() and similar calls won't set
 * updatedAt/lastModified/synced — the sync engine manages these itself.
 */
let _isSyncWrite = false

/**
 * Execute a DB write inside a sync-write context.
 * The Dexie updating hooks will be skipped so the sync engine
 * can set `synced`, `updatedAt`, etc. without interference.
 */
export async function withSyncWrite<T>(fn: () => Promise<T>): Promise<T> {
  _isSyncWrite = true
  try {
    return await fn()
  } finally {
    _isSyncWrite = false
  }
}

export class HappyTaskDB extends Dexie {
  tasks!: Table<Task>
  sections!: Table<Section>
  settings!: Table<Setting>
  userProfiles!: Table<UserProfile>
  timeEntries!: Table<TimeEntry>
  tags!: Table<Tag>
  projects!: Table<Project>
  goals!: Table<Goal>
  syncMetadata!: Table<SyncMetadata>
  syncQueue!: Table<SyncQueueItem>
  avatarCategories!: Table<AvatarCategory>
  avatars!: Table<Avatar>

  constructor() {
    super(DB_NAME)

    // Version 2: Base schema
    this.version(2).stores({
      tasks: 'id, title, status, priority, category, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, startDate, endDate, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt'
    })

    // Version 3: Add avatar tables
    this.version(3).stores({
      tasks: 'id, title, status, priority, category, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, startDate, endDate, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active'
    })

    // Version 4: Add cached image support for avatars
    this.version(4).stores({
      tasks: 'id, title, status, priority, category, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, startDate, endDate, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 5: Add lifeSpace, userId, cloudId, order, deletedAt to projects
    this.version(5).stores({
      tasks: 'id, title, status, priority, category, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 6: Add projectId, userId, cloudId, deletedAt to tasks
    this.version(6).stores({
      tasks: 'id, title, status, priority, category, projectId, userId, cloudId, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 7: Add syncQueue table (consolidated from old HappyTaskDB_v3)
    this.version(7).stores({
      tasks: 'id, title, status, priority, category, projectId, userId, cloudId, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      syncQueue: '++id, synced, timestamp, table',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 8: Add sections table + sectionId on tasks
    this.version(8).stores({
      tasks: 'id, title, status, priority, category, projectId, sectionId, userId, cloudId, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      sections: 'id, projectId, order, deletedAt',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      syncQueue: '++id, synced, timestamp, table',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 9: Add cloudId + synced indexes to sections (required by sync-pull duplicate guard)
    this.version(9).stores({
      sections: 'id, projectId, cloudId, order, synced, deletedAt',
    })

    // Version 10: Add cloudId, userId, deletedAt to tags for sync support
    this.version(10).stores({
      tags: 'id, name, usageCount, userId, cloudId, deletedAt, createdAt, updatedAt, lastModified, synced',
    })

    // Version 11: Backfill startDate/startTime/startPeriod on existing tasks
    this.version(11).stores({}).upgrade(tx => {
      return tx.table('tasks').toCollection().modify(task => {
        if (!('startDate' in task)) task.startDate = null
        if (!('startTime' in task)) task.startTime = null
        if (!('startPeriod' in task)) task.startPeriod = null
      })
    })

    // Version 12: Backfill dueTime/duePeriod on existing tasks
    this.version(12).stores({}).upgrade(tx => {
      return tx.table('tasks').toCollection().modify(task => {
        if (!('dueTime' in task)) task.dueTime = null
        if (!('duePeriod' in task)) task.duePeriod = null
      })
    })

    // Version 13: Backfill isSystem=false / systemKind=null on existing projects
    this.version(13).stores({}).upgrade(tx => {
      return tx.table('projects').toCollection().modify(project => {
        if (!('isSystem' in project)) project.isSystem = false
        if (!('systemKind' in project)) project.systemKind = null
      })
    })

    // Version 14: Backfill pausedAt=null on existing tasks (Clarify-Mode "Ruhen lassen").
    // No new stores() needed — column is not indexed (app only writes it, doesn't query).
    this.version(14).stores({}).upgrade(tx => {
      return tx.table('tasks').toCollection().modify(task => {
        if (!('pausedAt' in task)) task.pausedAt = null
      })
    })

    // Version 15: Index `startDate` on tasks so the UpcomingPage can do
    // range-scoped queries (`.where('startDate').between(startMonth, endMonth)`)
    // instead of a full-table scan. `dueDate` was already indexed.
    this.version(15).stores({
      tasks: 'id, title, status, priority, category, projectId, sectionId, userId, cloudId, startDate, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
    })

    // Version 16: Add `lifeSpace` to tasks (indexed so Kurz-notiert can query
    // orphan-per-lifespace efficiently). Backfill strategy:
    //   1. task.projectId resolves to a project → task.lifeSpace = project.lifeSpace
    //   2. orphan or project missing/soft-deleted → 'personal'
    // See also supabase/migrations/023_add_task_life_space.sql (mirrors this).
    this.version(16).stores({
      tasks: 'id, title, status, priority, category, lifeSpace, projectId, sectionId, userId, cloudId, startDate, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
    }).upgrade(async tx => {
      const projects = await tx.table('projects').toArray()
      const projectLifeSpace = new Map<string, string>(
        projects.filter(p => !p.deletedAt).map(p => [p.id, p.lifeSpace]),
      )
      return tx.table('tasks').toCollection().modify(task => {
        if (task.lifeSpace) return
        const fromProject = task.projectId ? projectLifeSpace.get(task.projectId) : undefined
        task.lifeSpace = fromProject ?? 'personal'
      })
    })

    // Version 17: Retire the per-lifespace `quick-notes` system projects.
    // Kurz-notiert becomes "orphan tasks with matching life_space" — no
    // system project needed. Unlink all tasks that referenced a quick-notes
    // system project, then soft-delete the projects themselves so sync
    // propagates the removal. Mirrors supabase/migrations/024.
    this.version(17).stores({}).upgrade(async tx => {
      const systemProjects = await tx.table('projects')
        .filter(p => p.systemKind === 'quick-notes' && !p.deletedAt)
        .toArray()
      if (systemProjects.length === 0) return
      const systemIds = new Set(systemProjects.map(p => p.id))
      await tx.table('tasks').toCollection().modify(task => {
        if (task.projectId && systemIds.has(task.projectId)) {
          task.projectId = null
        }
      })
      const now = new Date()
      await tx.table('projects').toCollection().modify(project => {
        if (systemIds.has(project.id)) {
          project.deletedAt = now
          project.updatedAt = now
          project.lastModified = now
          project.synced = false
        }
      })
    })

    // Version 18: Add goals table (Ziele feature). A Goal is a separate
    // entity from Task — see CONTEXT.md. Indexed on lifeSpace (page query),
    // cloudId + synced (sync-pull duplicate guard), createdAt (sort order).
    this.version(18).stores({
      goals: 'id, lifeSpace, userId, cloudId, createdAt, synced, deletedAt',
    })

    // Hook to auto-update timestamps and sync fields
    this.tasks.hook('creating', (primaryKey, obj, trans) => {
      obj.id = obj.id || crypto.randomUUID()
      obj.order = obj.order ?? 0
      if (_isSyncWrite) return  // Sync engine provides its own timestamps & synced flag
      const now = new Date()
      obj.createdAt = now
      obj.updatedAt = now
      obj.lastModified = now
      obj.synced = false
      obj.syncedAt = null
    })

    this.tasks.hook('updating', (modifications, _primaryKey, _obj, _trans) => {
      if (_isSyncWrite) return  // Sync engine manages timestamps & synced flag itself
      const now = new Date()
      ;(modifications as any).updatedAt = now
      ;(modifications as any).lastModified = now
      ;(modifications as any).synced = false
    })

    // Normalize tasks on read: ensure startDate/startTime/startPeriod are never undefined.
    // This handles objects written by db.tasks.put() (sync/undo) that may lack these fields.
    // Apply similar hooks to all tables
    const applyHooks = (table: Table) => {
      table.hook('creating', (_primaryKey, obj, _trans) => {
        obj.id = obj.id || crypto.randomUUID()
        if (_isSyncWrite) return  // Sync engine provides its own timestamps & synced flag
        const now = new Date()
        obj.createdAt = now
        obj.updatedAt = now
        obj.lastModified = now
        obj.synced = false
        obj.syncedAt = null
      })

      table.hook('updating', (modifications, _primaryKey, _obj, _trans) => {
        if (_isSyncWrite) return  // Sync engine manages timestamps & synced flag itself
        const now = new Date()
        ;(modifications as any).updatedAt = now
        ;(modifications as any).lastModified = now
        ;(modifications as any).synced = false
      })
    }

    applyHooks(this.settings)
    applyHooks(this.userProfiles)
    applyHooks(this.timeEntries)
    applyHooks(this.tags)
    applyHooks(this.projects)
    applyHooks(this.sections)
    applyHooks(this.goals)
  }

  // Task operations
  async createTask(taskData: CreateTask & { id?: string }): Promise<Task> {
    const task: Task = {
      ...taskData,
      id: taskData.id ?? crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      attention: taskData.attention ?? DEFAULT_TASK_ATTENTION,
      status: taskData.status || DEFAULT_TASK_STATUS,
      category: taskData.category || DEFAULT_TASK_CATEGORY,
      lifeSpace: taskData.lifeSpace,
      projectId: taskData.projectId ?? null,
      sectionId: taskData.sectionId ?? null,
      userId: null,
      cloudId: null,
      deletedAt: null,
      // Respect incoming pausedAt so duplicating a paused task keeps it
      // in Ruhend. Defaults to null for fresh tasks.
      pausedAt: taskData.pausedAt ?? null,
      durationMode: taskData.durationMode ?? null,
      startDate: taskData.startDate ?? null,
      startTime: taskData.startTime ?? null,
      startPeriod: taskData.startPeriod ?? null,
      dueTime: taskData.dueTime ?? null,
      duePeriod: taskData.duePeriod ?? null,
      order: taskData.order ?? await this.getNextTaskOrder()
    }
    
    await this.tasks.add(task)

    // Queue for cloud sync
    await this.queueSyncChange('create', 'tasks', { localId: task.id })

    return task
  }

  async updateTask(id: string, updates: Partial<UpdateTask>): Promise<Task | null> {
    await this.tasks.update(id, updates)
    const raw = await this.tasks.get(id)
    const updated = raw ? this.normalizeTask(raw) : null

    // Queue for cloud sync
    if (updated) {
      await this.queueSyncChange('update', 'tasks', { localId: id })
    }

    return updated
  }

  /** Batch-update order (and optionally sectionId) for multiple tasks in a single transaction. */
  async reorderTasks(updates: Array<{ id: string; order: number; sectionId?: string | null }>): Promise<void> {
    const now = new Date()
    await this.transaction('rw', this.tasks, this.syncQueue, async () => {
      for (const { id, order, sectionId } of updates) {
        const fields: Record<string, unknown> = {
          order,
          updatedAt: now,
          lastModified: now,
          synced: false,
        }
        if (sectionId !== undefined) fields.sectionId = sectionId
        await this.tasks.update(id, fields)
        await this.queueSyncChange('update', 'tasks', { localId: id })
      }
    })
  }

  async deleteTask(id: string): Promise<void> {
    const task = await this.tasks.get(id)
    await this.tasks.update(id, { deletedAt: new Date() })

    // Queue for cloud sync
    await this.queueSyncChange('delete', 'tasks', { localId: id, cloudId: task?.cloudId })
  }

  /** Ensure task objects always have startDate/startTime/startPeriod as null (not undefined).
   *  Needed because db.tasks.put() from sync/undo can strip fields absent from the written object. */
  private normalizeTask(task: Task): Task {
    let normalized = task
    if (!('startDate' in normalized)) {
      normalized = { ...normalized, startDate: null, startTime: null, startPeriod: null }
    }
    if (!('dueTime' in normalized)) {
      normalized = { ...normalized, dueTime: null, duePeriod: null }
    }
    if (!('pausedAt' in normalized)) {
      normalized = { ...normalized, pausedAt: null }
    }
    return normalized
  }

  async getTask(id: string): Promise<Task | null> {
    const task = await this.tasks.get(id)
    return task ? this.normalizeTask(task) : null
  }

  async getAllTasks(): Promise<Task[]> {
    const tasks = await this.tasks.orderBy('order').filter(t => !t.deletedAt).toArray()
    return tasks.map(t => this.normalizeTask(t))
  }

  async getTasksByStatus(status: Task['status']): Promise<Task[]> {
    const tasks = await this.tasks.where('status').equals(status).toArray()
    return tasks.map(t => this.normalizeTask(t))
  }

  async getTasksByCategory(category: Task['category']): Promise<Task[]> {
    const tasks = await this.tasks.where('category').equals(category).toArray()
    return tasks.map(t => this.normalizeTask(t))
  }

  async getTasksByProject(projectId: string): Promise<Task[]> {
    const tasks = await this.tasks
      .where('projectId')
      .equals(projectId)
      .filter(t => !t.deletedAt)
      .sortBy('order')
    return tasks.map(t => this.normalizeTask(t))
  }

  /**
   * Tasks that belong in "Kurz notiert" for a given lifespace:
   *   projectId == null && lifeSpace == X && status != 'completed'
   *   && pausedAt == null && startDate == null && !deletedAt
   *
   * Added in PR 1 of the `Task.lifeSpace` migration — unused until PR 2
   * repoints QuickNotesPage away from the `quick-notes` system project.
   */
  async getQuickNotesByLifeSpace(
    lifeSpace: 'personal' | 'work' | 'home',
  ): Promise<Task[]> {
    const tasks = await this.tasks
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(t =>
        t.projectId == null
        && !t.deletedAt
        && t.status !== 'completed'
        && t.pausedAt == null
        && t.startDate == null,
      )
      .sortBy('order')
    return tasks.map(t => this.normalizeTask(t))
  }

  async completeTask(id: string): Promise<Task | null> {
    const now = new Date()
    await this.tasks.update(id, {
      status: 'completed',
      completedAt: now,
    })
    // Queue for cloud sync — without this, the next pull cycle would
    // overwrite the local completion with the stale cloud copy
    // (status=todo) because no syncQueue entry tells the engine that
    // this row needs to be pushed first.
    await this.queueSyncChange('update', 'tasks', { localId: id })
    return await this.getTask(id)
  }

  private async getNextTaskOrder(): Promise<number> {
    const lastTask = await this.tasks.orderBy('order').last()
    return (lastTask?.order || 0) + 1
  }

  // Setting operations
  async createSetting(settingData: CreateSetting): Promise<Setting> {
    const setting: Setting = {
      ...settingData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null
    }
    
    await this.settings.add(setting)
    return setting
  }

  async getSetting(key: string): Promise<Setting | null> {
    return await this.settings.where('key').equals(key).first() || null
  }

  async getSettingValue<T = any>(key: string, defaultValue?: T): Promise<T> {
    const setting = await this.getSetting(key)
    return setting ? setting.value : defaultValue
  }

  async setSetting(key: string, value: any, type: Setting['type'] = 'string', description?: string): Promise<Setting> {
    const existing = await this.getSetting(key)
    
    if (existing) {
      await this.settings.update(existing.id, { value, type, description })
      return await this.getSetting(key) as Setting
    } else {
      return await this.createSetting({
        key,
        value,
        type,
        description: description || null,
        isUserSetting: true
      })
    }
  }

  async getAllSettings(): Promise<Setting[]> {
    return await this.settings.toArray()
  }

  // User Profile operations
  async createUserProfile(profileData: CreateUserProfile): Promise<UserProfile> {
    const profile: UserProfile = {
      ...profileData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      usagePurposes: profileData.usagePurposes || [],
      userIntents: profileData.userIntents || [],
      preferences: { ...DEFAULT_USER_PREFERENCES, ...profileData.preferences },
      statistics: { ...DEFAULT_USER_STATISTICS, ...profileData.statistics }
    }

    await this.userProfiles.add(profile)
    return profile
  }

  async getCurrentUserProfile(): Promise<UserProfile | null> {
    return await this.userProfiles.orderBy('createdAt').first() || null
  }

  async updateUserProfile(id: string, updates: Partial<UpdateUserProfile>): Promise<UserProfile | null> {
    await this.userProfiles.update(id, updates)
    return await this.userProfiles.get(id) || null
  }

  // Time Entry operations
  async createTimeEntry(entryData: CreateTimeEntry): Promise<TimeEntry> {
    const entry: TimeEntry = {
      ...entryData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null
    }
    
    await this.timeEntries.add(entry)
    return entry
  }

  async getTimeEntriesForTask(taskId: string): Promise<TimeEntry[]> {
    return await this.timeEntries.where('taskId').equals(taskId).toArray()
  }

  async getActiveTimeEntry(): Promise<TimeEntry | null> {
    return await this.timeEntries.where('endTime').equals(null).first() || null
  }

  async stopTimeEntry(id: string): Promise<TimeEntry | null> {
    const endTime = new Date()
    const entry = await this.timeEntries.get(id)
    
    if (entry && entry.startTime) {
      const duration = Math.round((endTime.getTime() - entry.startTime.getTime()) / 60000) // minutes
      await this.timeEntries.update(id, { 
        endTime, 
        duration 
      })
      return await this.timeEntries.get(id) || null
    }
    
    return null
  }

  // Tag operations
  async createTag(tagData: CreateTag): Promise<Tag> {
    const tag: Tag = {
      ...tagData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      usageCount: tagData.usageCount || 0,
      userId: null,
      cloudId: null,
      deletedAt: null,
    }

    await this.tags.add(tag)
    await this.queueSyncChange('create', 'tags', { localId: tag.id })
    return tag
  }

  async getTag(tagId: string): Promise<Tag | undefined> {
    return await this.tags.get(tagId)
  }

  async getAllTags(): Promise<Tag[]> {
    return await this.tags.orderBy('name').toArray()
  }

  async updateTag(id: string, updates: Partial<Omit<Tag, 'id'>>): Promise<void> {
    await this.tags.update(id, updates)
    await this.queueSyncChange('update', 'tags', { localId: id })
  }

  async deleteTag(id: string): Promise<void> {
    const tag = await this.tags.get(id)
    await this.tags.update(id, { deletedAt: new Date() })
    await this.queueSyncChange('delete', 'tags', { localId: id, cloudId: tag?.cloudId })
  }

  async getOrCreateTag(name: string, color?: string): Promise<Tag> {
    const existing = await this.tags.where('name').equals(name).first()

    if (existing) {
      return existing
    }

    return await this.createTag({
      name,
      color: color || '#3B82F6',
      description: null
    })
  }

  // Project operations
  async createProject(projectData: CreateProject): Promise<Project> {
    const project: Project = {
      ...projectData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      userId: projectData.userId ?? null,
      cloudId: null,
      deletedAt: null,
      order: projectData.order ?? await this.getMinProjectOrder(projectData.lifeSpace) - 1,
      isSystem: projectData.isSystem ?? false,
      systemKind: projectData.systemKind ?? null,
    }

    await this.projects.add(project)

    // Queue for cloud sync
    await this.queueSyncChange('create', 'projects', { localId: project.id })

    return project
  }

  async updateProject(id: string, updates: Partial<UpdateProject>): Promise<Project | null> {
    await this.projects.update(id, {
      ...updates,
      updatedAt: new Date(),
    })
    const updated = await this.projects.get(id) || null

    // Queue for cloud sync
    if (updated) {
      await this.queueSyncChange('update', 'projects', { localId: id })
    }

    return updated
  }

  async deleteProject(id: string): Promise<void> {
    const project = await this.projects.get(id)
    await this.projects.update(id, { deletedAt: new Date() })

    // Cascade: soft-delete all tasks belonging to this project
    const tasks = await this.tasks
      .where('projectId')
      .equals(id)
      .filter(t => !t.deletedAt)
      .toArray()
    for (const task of tasks) {
      await this.tasks.update(task.id, { deletedAt: new Date() })
      await this.queueSyncChange('delete', 'tasks', { localId: task.id, cloudId: task.cloudId })
    }

    // Queue for cloud sync
    await this.queueSyncChange('delete', 'projects', { localId: id, cloudId: project?.cloudId })
  }

  async getAllProjects(): Promise<Project[]> {
    // System projects (quick-notes etc.) are hidden from general listings.
    return await this.projects
      .filter(p => !p.isSystem)
      .sortBy('name')
  }

  async getProjectsByLifeSpace(lifeSpace: string): Promise<Project[]> {
    const results = await this.projects
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(p => !p.deletedAt && !p.isSystem)
      .sortBy('order')
    return results
  }

  async getProject(id: string): Promise<Project | null> {
    return await this.projects.get(id) || null
  }

  /** Look up the system project for a given lifeSpace + kind (e.g. "quick-notes").
   *  System projects are created on first access and hidden from the regular project list. */
  async getSystemProject(lifeSpace: string, systemKind: string): Promise<Project | null> {
    const results = await this.projects
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(p => p.isSystem === true && p.systemKind === systemKind && !p.deletedAt)
      .toArray()
    return results[0] ?? null
  }

  private async getNextProjectOrder(lifeSpace: string): Promise<number> {
    const projects = await this.getProjectsByLifeSpace(lifeSpace)
    if (projects.length === 0) return 0
    return Math.max(...projects.map(p => p.order)) + 1
  }

  private async getMinProjectOrder(lifeSpace: string): Promise<number> {
    const projects = await this.getProjectsByLifeSpace(lifeSpace)
    if (projects.length === 0) return 1
    return Math.min(...projects.map(p => p.order))
  }

  // Section operations
  async createSection(sectionData: CreateSection): Promise<Section> {
    const section: Section = {
      ...sectionData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      userId: null,
      cloudId: null,
      deletedAt: null,
      collapsed: sectionData.collapsed ?? false,
      order: sectionData.order ?? await this.getNextSectionOrder(sectionData.projectId),
    }

    await this.sections.add(section)

    // Queue for cloud sync
    await this.queueSyncChange('create', 'sections', { localId: section.id })

    return section
  }

  async updateSection(id: string, updates: Partial<UpdateSection>): Promise<Section | null> {
    await this.sections.update(id, updates)
    const updated = await this.sections.get(id) || null

    if (updated) {
      await this.queueSyncChange('update', 'sections', { localId: id })
    }

    return updated
  }

  async deleteSection(id: string): Promise<void> {
    await this.sections.update(id, { deletedAt: new Date() })

    // Unlink tasks from this section (set sectionId to null)
    const tasks = await this.tasks
      .where('sectionId')
      .equals(id)
      .filter(t => !t.deletedAt)
      .toArray()
    for (const task of tasks) {
      await this.tasks.update(task.id, { sectionId: null })
      await this.queueSyncChange('update', 'tasks', { localId: task.id })
    }

    await this.queueSyncChange('delete', 'sections', { localId: id })
  }

  async getSection(id: string): Promise<Section | null> {
    return await this.sections.get(id) || null
  }

  async getSectionsByProject(projectId: string): Promise<Section[]> {
    return await this.sections
      .where('projectId')
      .equals(projectId)
      .filter(s => !s.deletedAt)
      .sortBy('order')
  }

  /**
   * Get interleaved list of tasks and sections for a project, sorted by order.
   * Each item is tagged with `type: 'task' | 'section'` for rendering.
   */
  async getProjectItems(projectId: string): Promise<({ type: 'task' } & Task | { type: 'section' } & Section)[]> {
    const [tasks, sections] = await Promise.all([
      this.getTasksByProject(projectId),
      this.getSectionsByProject(projectId),
    ])

    const items: ({ type: 'task' } & Task | { type: 'section' } & Section)[] = [
      ...tasks.map(t => ({ type: 'task' as const, ...t })),
      ...sections.map(s => ({ type: 'section' as const, ...s })),
    ]

    items.sort((a, b) => a.order - b.order)
    return items
  }

  private async getNextSectionOrder(projectId: string): Promise<number> {
    const items = await this.getProjectItems(projectId)
    if (items.length === 0) return 0
    return Math.max(...items.map(i => i.order)) + 1
  }

  // Goal operations
  // A Goal is a statement of direction for one life space — NOT a task.
  // See CONTEXT.md. Separate entity, separate table.
  async createGoal(goalData: CreateGoal & { id?: string }): Promise<Goal> {
    const goal: Goal = {
      ...goalData,
      id: goalData.id ?? crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      description: goalData.description ?? null,
      userId: goalData.userId ?? null,
      cloudId: null,
      deletedAt: null,
    }

    await this.goals.add(goal)

    // Queue for cloud sync
    await this.queueSyncChange('create', 'goals', { localId: goal.id })

    return goal
  }

  async updateGoal(id: string, updates: Partial<UpdateGoal>): Promise<Goal | null> {
    await this.goals.update(id, updates)
    const updated = await this.goals.get(id) || null

    // Queue for cloud sync
    if (updated) {
      await this.queueSyncChange('update', 'goals', { localId: id })
    }

    return updated
  }

  async deleteGoal(id: string): Promise<void> {
    const goal = await this.goals.get(id)
    await this.goals.update(id, { deletedAt: new Date() })

    // Queue for cloud sync
    await this.queueSyncChange('delete', 'goals', { localId: id, cloudId: goal?.cloudId })
  }

  async getGoal(id: string): Promise<Goal | null> {
    return await this.goals.get(id) || null
  }

  /** Goals for a life space, newest-first (Grilling decision #2 — no manual ordering). */
  async getGoalsByLifeSpace(lifeSpace: LifeSpace): Promise<Goal[]> {
    const goals = await this.goals
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(g => !g.deletedAt)
      .toArray()
    return goals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  }

  // Sync queue helper — queues a change for cloud sync
  async queueSyncChange(
    action: 'create' | 'update' | 'delete',
    table: 'tasks' | 'settings' | 'projects' | 'sections' | 'tags' | 'goals',
    data: any
  ): Promise<void> {
    try {
      await this.syncQueue.add({
        action,
        table,
        recordId: 0,
        data,
        synced: false,
        timestamp: new Date(),
      })
    } catch (error) {
      console.error('[SyncQueue] Failed to queue change:', error)
    }
  }

  // Utility methods
  async clearAllData(): Promise<void> {
    await this.transaction('rw', [this.tasks, this.sections, this.settings, this.userProfiles, this.timeEntries, this.tags, this.projects, this.goals, this.syncMetadata, this.syncQueue, this.avatarCategories, this.avatars], () => {
      this.tasks.clear()
      this.sections.clear()
      this.settings.clear()
      this.userProfiles.clear()
      this.timeEntries.clear()
      this.tags.clear()
      this.projects.clear()
      this.goals.clear()
      this.syncMetadata.clear()
      this.syncQueue.clear()
      this.avatarCategories.clear()
      this.avatars.clear()
    })
  }

  async getUnSyncedItems(): Promise<{
    tasks: Task[]
    sections: Section[]
    settings: Setting[]
    userProfiles: UserProfile[]
    timeEntries: TimeEntry[]
    tags: Tag[]
    projects: Project[]
    goals: Goal[]
  }> {
    return {
      tasks: await this.tasks.where('synced').equals(false).toArray(),
      sections: await this.sections.where('synced').equals(false).toArray(),
      settings: await this.settings.where('synced').equals(false).toArray(),
      userProfiles: await this.userProfiles.where('synced').equals(false).toArray(),
      timeEntries: await this.timeEntries.where('synced').equals(false).toArray(),
      tags: await this.tags.where('synced').equals(false).toArray(),
      projects: await this.projects.where('synced').equals(false).toArray(),
      goals: await this.goals.where('synced').equals(false).toArray()
    }
  }

  async markAsSynced(tableName: string, id: string): Promise<void> {
    const now = new Date()
    const table = (this as any)[tableName] as Table
    if (table) {
      await table.update(id, { 
        synced: true, 
        syncedAt: now 
      })
    }
  }
}

// Create singleton instance
export const db = new HappyTaskDB()